import React from 'react';
import { css, Global } from '@emotion/react';
import styled from '@emotion/styled';
import { Dialog, DialogContent, DialogTitle, Button, Typography } from '@mui/material';
import Cookies from 'js-cookie';
import { v4 as uuidv4 } from 'uuid';
import { useFetchPrompt } from './hooks/use-fetch-prompts';
import { YellowButton } from '@/components/button/button';
import { useAuth0 } from '@/auth';
import { useUpdateUserClbcMutation } from '@/generated/graphql';
import { useCurrentUser } from '@/hooks/use-current-user/use-current-user';
import { track } from '../analytics/track';
import { ClientAnalyticsEventType } from '../analytics/constants';

type ClbcPromptModalProps = {
  open: boolean;
  onClose: () => void;
};

const MainDialog = styled(Dialog)`
  z-index: 9999;
  .MuiDialog-paper {
    border-radius: 8px;
    padding: 30px;
    min-width: 300px;
  }
`;

const Title = styled(Typography)`
  font-family: 'Work Sans', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 24px;
  line-height: 130%;
  color: #231727;
  margin-bottom: 16px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
`;

const ClbcPromptModal = ({ open, onClose }: ClbcPromptModalProps) => {
  const promptText = useFetchPrompt();
  const { isAuthenticated } = useAuth0();
  const { data: currentUser } = useCurrentUser();
  const [updateUserProfile] = useUpdateUserClbcMutation({
    refetchQueries: ['CurrentUser'],
  });
  //const [updateAttendee] = useUpdateAttendeeMutation();

  const handleResponse = async (hasRelationship: boolean) => {
    const guid = uuidv4();
    const value = JSON.stringify({ hasRelationship, guid });
    Cookies.set('clbcResponse', value);
    track(ClientAnalyticsEventType.CLBC_PROMPT_RESPONSE, {
      hasRelationship: hasRelationship.toString(),
      guid,
    });
    if (isAuthenticated && currentUser?.id) {
      // Update the user's profile with the response
      // This is a placeholder for the actual implementation
      try {
        await updateUserProfile({
          variables: {
            id: currentUser.id,
            user: {
              hasRelationshipWithCLBC: hasRelationship,
            },
          },
        });
      } catch (error) {
        console.error('Error updating user profile:', error);
      }
    }
    onClose();
  };

  return (
    <MainDialog open={open} onClose={onClose}>
      <Global
        styles={css`
          .MuiDialog-paper {
            box-shadow: 0px 3px 6px rgba(0, 0, 0, 0.16);
          }
        `}
      />
      <DialogTitle component="div">
        <Title>{promptText || 'Do you have a relationship with CLBC?'}</Title>
      </DialogTitle>
      <DialogContent>
        <ButtonRow>
          <YellowButton variant="contained" onClick={() => handleResponse(true)}>
            Yes
          </YellowButton>
          <YellowButton variant="outlined" onClick={() => handleResponse(false)}>
            No
          </YellowButton>
        </ButtonRow>
      </DialogContent>
    </MainDialog>
  );
};

export default ClbcPromptModal;
